import { ClearOutlined, PictureOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { MouseEvent, TouchEvent, useEffect, useRef, useState } from 'react';

interface SignaturePadProps {
  valueSignature?: string; // URL or base64 data URL from the database
  canUploadSignature?: boolean;
  onSignatureDrawn: (dataUrl: string) => void;
}

const SignaturePad: React.FC<SignaturePadProps> = ({ valueSignature, canUploadSignature, onSignatureDrawn }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [scrollDisabled, setScrollDisabled] = useState<boolean>(false);

  const loadImage = (imageSrc: string) => {
    const image = new Image();
    image.onload = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
    };
    image.src = imageSrc;
  };

  useEffect(() => {
    // Load the signature image if provided
    if (valueSignature) {
      loadImage(valueSignature);
    }
  }, [valueSignature]);

  // Update overflow on "main", "div", and "body" elements when scroll is toggled
  useEffect(() => {
    const mainElement = document.querySelector('main') || document.querySelector('div');

    const updateOverflow = () => {
      if (mainElement) {
        if (scrollDisabled) {
          mainElement.style.overflow = 'hidden';
        } else {
          mainElement.style.overflow = '';
        }
      }
      document.body.style.overflow = scrollDisabled ? 'hidden' : '';
    };

    updateOverflow();

    return () => {
      if (mainElement) {
        mainElement.style.overflow = '';
      }
      document.body.style.overflow = '';
    };
  }, [scrollDisabled]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      loadImage(URL.createObjectURL(file));
    }
  };

  const getCoordinates = (event: any) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    const scaleX = canvasRef.current!.width / rect.width;
    const scaleY = canvasRef.current!.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const { x, y } = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#5068aa';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
    setIsDrawing(true);
  };

  const endDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      const signatureData = canvasRef.current.toDataURL();
      onSignatureDrawn(signatureData);
    }
  };

  const draw = (event: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    const { x, y } = getCoordinates(event);
    const ctx = canvasRef.current.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const clearCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      onSignatureDrawn('');
    }
  };

  const toggleScroll = () => {
    setScrollDisabled((prev) => !prev);
  };

  return (
    <>
      <canvas
        ref={canvasRef}
        width={400}
        height={200}
        style={{ width: '100%', border: '1px solid #d9d9d9' }}
        onTouchStart={startDrawing}
        onTouchEnd={endDrawing}
        onTouchMove={draw}
        onMouseDown={startDrawing}
        onMouseUp={endDrawing}
        onMouseMove={draw}
      />
      <Button icon={<ClearOutlined />} onClick={clearCanvas}>
        Resetează
      </Button>
      {canUploadSignature && (
        <Button icon={<PictureOutlined />} onClick={() => fileInputRef.current?.click()} style={{ marginLeft: '20px' }}>
          Încarcă Semnătura
        </Button>
      )}
      <input type='file' accept='image/*' style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileChange} />
      <Button onClick={toggleScroll} style={{ marginLeft: '20px' }}>
        {scrollDisabled ? 'Start Scroll' : 'Stop Scroll'}
      </Button>
    </>
  );
};

export default SignaturePad;
